(ns storrito.editor.music-sticker
  (:require [renderer6.music-sticker-constants :as constants]))

;; Concept:
;;
;; FabricJS previews of the native Instagram music sticker visuals
;; (display types). On the published story the Instagram app renders
;; these visuals itself; the previews here replicate them so that the
;; editor and the rendered story media are WYSIWYG. All layout
;; constants below are measured from stories that were posted with the
;; Instagram app (soul.globe, 2026-08-19, 720x1280 output) — see
;; docs/music-sticker.md for the measurement details.

(defn cover-image
  "A FabricJS object for the square cover artwork, clipped to rounded
   corners (`radius` in target pixels) or a circle. Falls back to a
   gray placeholder when no cover image is available."
  [cover-img {:keys [left top size circle? radius]}]
  (if cover-img
    (let [natural (max 1 (.-naturalWidth cover-img))
          scale (/ size natural)
          r (if radius
              (/ radius scale)
              (* natural 0.12))]
      (js/fabric.Image.
        cover-img
        (clj->js {:left left
                  :top top
                  :scaleX scale
                  :scaleY scale
                  :clipPath (if circle?
                              (js/fabric.Circle.
                                #js {:radius (/ natural 2)
                                     :originX "center"
                                     :originY "center"})
                              (js/fabric.Rect.
                                #js {:width natural
                                     :height natural
                                     :rx r
                                     :ry r
                                     :originX "center"
                                     :originY "center"}))})))
    (js/fabric.Rect.
      (clj->js (merge {:left left
                       :top top
                       :width size
                       :height size
                       :fill "#d8d8d8"}
                      (if circle?
                        {:rx (/ size 2)
                         :ry (/ size 2)}
                        {:rx (or radius (* size 0.12))
                         :ry (or radius (* size 0.12))}))))))

(def group-options
  {:lockScalingFlip true
   :min-scale 0.1
   :max-scale 5.0
   :dirty true
   :top 0
   :left 0
   :scaleX 1
   :scaleY 1})

(defn- centered!
  [text-obj total-width top]
  (set! (.-left text-obj)
        (/ (- total-width (.getScaledWidth text-obj))
           2))
  (set! (.-top text-obj) top)
  text-obj)

(defn- truncate-to-width!
  "Shortens the text object's text with a trailing ellipsis until it
   fits into `max-width`. The font size stays unchanged."
  [text-obj max-width]
  (when (> (.-width text-obj) max-width)
    (let [codepoints (js/Array.from (.-text text-obj))
          set-text! (fn [n]
                      (.set text-obj
                            "text"
                            (str (.trimEnd (.join (.slice codepoints 0 n) ""))
                                 "…"))
                      (.-width text-obj))]
      ;; Start from a proportional estimate, then shrink until it fits:
      (loop [n (js/Math.floor (* (.-length codepoints)
                                 (/ max-width (.-width text-obj))))]
        (when (and (> n 1)
                   (> (set-text! n) max-width))
          (recur (dec n))))))
  text-obj)

(defn- wrap-two-lines
  "Greedily word-wraps `text` at `max-width` (measured with a
   fabric.Text using `opts`) into at most two lines. Returns
   [line1 line2-or-nil]; a second line that still overflows must be
   shortened with `truncate-to-width!` by the caller."
  [text max-width opts]
  (let [measurer (js/fabric.Text. "" opts)
        width-of (fn [s]
                   (.set measurer "text" s)
                   (.-width measurer))]
    (if (<= (width-of text) max-width)
      [text nil]
      (let [words (.split text " ")
            n (loop [n 1]
                (if (and (< n (.-length words))
                         (<= (width-of (.join (.slice words 0 (inc n)) " "))
                             max-width))
                  (recur (inc n))
                  n))]
        [(.join (.slice words 0 n) " ")
         (let [rest (.join (.slice words n) " ")]
           (when (seq rest)
             rest))]))))

(defn- two-line-text
  "At most two centered lines of `text`, greedily word-wrapped at
   `max-width`; the second line (or a single overlong word) is
   shortened with an ellipsis. Returns the positioned fabric.Text
   objects, starting at `top` with `line-spacing` between line tops."
  [{:keys [text opts w top line-spacing max-width]}]
  (let [[line1 line2] (wrap-two-lines (or text "")
                                      max-width
                                      opts)]
    (map-indexed
      (fn [i line]
        (-> (js/fabric.Text. line opts)
            (truncate-to-width! max-width)
            (centered! w (+ top (* i line-spacing)))))
      (if line2
        [line1 line2]
        [line1]))))

;; --- The animated waveform glyph (album art and simple) ---
;;
;; A bouncing equalizer, measured from the MUSIC_OVERLAY_SIMPLE
;; thumbnail: three white capsules with fixed centers whose heights
;; oscillate sinusoidally (mean 0.311 of the thumbnail, amplitude
;; 0.106, period 1.16 s), the middle bar in antiphase to the outer
;; bars. public/img/music-waveform-bounce.webm holds one such loop
;; (330x330, 35 frames, VP9 with alpha). The editor composition plays
;; it over the rasterized sticker exactly like renderer6 bakes it into
;; the story media. Both display types use this animation (natively the
;; album-art badge shows a scrolling glyph loop instead, see
;; docs/music-sticker.md). The video's uri is in
;; `renderer6.music-sticker-constants/waveform`, shared with the
;; renderer.

;; --- MUSIC_VINYL ---
;;
;; The disc is a square with the sticker's full width at the top, the
;; title/artist text sits below. Measured: disc diameter 701 px, the
;; record rotates at exactly 36 deg/s (10 s per revolution); title and
;; artist have the same size (cap height 0.037 of the disc diameter,
;; so ~29 px Arial at our 560 px disc), the title is bold, the artist
;; regular; the title's ascender starts 0.100 disc diameters below the
;; disc, the two baselines are 0.070 disc diameters apart. The font
;; size is fixed: a long title word-wraps to at most two centered lines
;; (line tops 0.0597 disc diameters apart, wrap width ~0.94 of the
;; disc) and the second line is shortened with an ellipsis (measured
;; from a story with a long title); the artist follows the last title
;; line at the usual gap and is assumed to wrap the same way. The disc
;; geometry, rotation speed and record video are in
;; `renderer6.music-sticker-constants/vinyl`, shared with the renderer:
(def vinyl-width 560)
(def vinyl-max-text-ratio 0.94)
(def vinyl-line-spacing-ratio 0.0597)

;; The Instagram app does not allow shrinking the music stickers below
;; a minimal size (measured from stories with the sticker pinched to
;; its minimum). The editor clamps the clip scale accordingly (see
;; ui.editor2.actions/transform-clip). Vinyl and album art as a
;; fraction of the story width; the simple card's width varies with
;; the text, so its minimum is on the fixed card height as a fraction
;; of the story height:
(def vinyl-min-width-ratio 0.2317)
(def album-art-min-width-ratio 0.1530)
(def simple-min-height-ratio 0.02746)

(defn vinyl-sticker
  "The MUSIC_VINYL sticker without the record itself: the record is not
   rasterized because the extracted real record video (the :disc-uri of
   `renderer6.music-sticker-constants/vinyl`) is layered over the
   sticker, both in the editor composition and by renderer6. Rasterized are only the static cover label (which sits
   exactly under the rotating label overlay) and the title/artist text.
   An invisible square keeps the group's bounds identical to the
   record's footprint: a square with the sticker's full width at the
   top."
  [{:keys [title artist cover-img]}]
  (let [w vinyl-width
        r (/ w 2)
        label-size (* (:label-ratio constants/vinyl) w)
        label-r (/ label-size 2)
        disc-bounds (js/fabric.Rect.
                      #js {:left 0
                           :top 0
                           :width w
                           :height w
                           :fill "transparent"})
        cover (cover-image cover-img
                           {:left (- r label-r)
                            :top (- r label-r)
                            :size label-size
                            :circle? true})
        shadow "rgba(0,0,0,0.45) 0px 2px 10px"
        max-text-width (* vinyl-max-text-ratio w)
        line-spacing (* vinyl-line-spacing-ratio w)
        title-objs (two-line-text
                     {:text title
                      :opts #js {:fontFamily "Arial"
                                 :fontWeight "bold"
                                 :fontSize 29
                                 :fill "#ffffff"
                                 :shadow shadow}
                      :w w
                      :top (+ w 54)
                      :line-spacing line-spacing
                      :max-width max-text-width})
        ;; the block-to-block gap is the measured title-to-artist gap
        ;; minus one line spacing (39 - 33 at our 560 px disc):
        artist-top (+ w 54
                      (* (count title-objs) line-spacing)
                      6)
        artist-objs (two-line-text
                      {:text artist
                       :opts #js {:fontFamily "Arial"
                                  :fontSize 29
                                  :fill "#f3f3f3"
                                  :shadow shadow}
                       :w w
                       :top artist-top
                       :line-spacing line-spacing
                       :max-width max-text-width})]
    (js/fabric.Group. (into-array
                        (concat [disc-bounds cover]
                                title-objs
                                artist-objs))
                      (clj->js group-options))))

;; --- MUSIC_OVERLAY_ALBUM_ART ---
;;
;; A large square cover (the sticker's full width) with rounded
;; corners, a translucent dark badge with the animated waveform glyph
;; in its lower-left corner, and title/artist below the cover.
;; Measured at cover width 683 px: cover corner radius 0.0225; badge
;; 0.2431 wide/high, inset 0.0545 from the cover's left and bottom
;; edge, corner radius 0.0234, background rgba(0,0,0,0.2); title and
;; artist have the same size (cap height 0.0542, ~42 px Arial at our
;; 560 px cover), title bold white, artist regular white at 85%
;; opacity, no text shadow; the title's ascender starts 0.0747 below
;; the cover, the two baselines are 0.1069 apart. The font size is
;; fixed: a long title word-wraps to at most two centered lines (line
;; tops 0.0833 apart, wrap width ~0.94 of the cover) and the second
;; line is shortened with an ellipsis (measured from a story with a
;; long title); the artist follows the last title line and is assumed
;; to be shortened the same way. All ratios are of the cover width.
;; The badge geometry is in
;; `renderer6.music-sticker-constants/album-art`, shared with the
;; renderer:
(def album-art-width 560)
(def album-art-cover-radius-ratio 0.0225)
(def album-art-badge-radius-ratio 0.0234)
(def album-art-max-text-ratio 0.94)
(def album-art-line-spacing-ratio 0.0833)

(defn album-art-sticker
  "The MUSIC_OVERLAY_ALBUM_ART sticker without the waveform glyph: the
   glyph is not rasterized because the animated waveform video
   (`renderer6.music-sticker-constants/waveform`) is layered over the
   badge, both in the editor composition and by renderer6. Rasterized are the cover, the translucent badge
   background and the title/artist text."
  [{:keys [title artist cover-img]}]
  (let [w album-art-width
        badge (* (:badge-ratio constants/album-art) w)
        inset (* (:badge-inset-ratio constants/album-art) w)
        max-text-width (* album-art-max-text-ratio w)
        line-spacing (* album-art-line-spacing-ratio w)
        title-opts #js {:fontFamily "Arial"
                        :fontWeight "bold"
                        :fontSize 42
                        :fill "#ffffff"}
        cover (cover-image cover-img
                           {:left 0
                            :top 0
                            :size w
                            :radius (* album-art-cover-radius-ratio w)})
        badge-bg (js/fabric.Rect.
                   #js {:left inset
                        :top (- w inset badge)
                        :width badge
                        :height badge
                        :rx (* album-art-badge-radius-ratio w)
                        :ry (* album-art-badge-radius-ratio w)
                        :fill "rgba(0,0,0,0.2)"})
        title-objs (two-line-text
                     {:text title
                      :opts title-opts
                      :w w
                      :top (+ w 36)
                      :line-spacing line-spacing
                      :max-width max-text-width})
        ;; the block-to-block gap is the measured title-to-artist gap
        ;; minus one line spacing (60 - 47 at our 560 px cover):
        artist-top (+ w 36
                      (* (count title-objs) line-spacing)
                      13)
        artist-objs (two-line-text
                      {:text artist
                       :opts #js {:fontFamily "Arial"
                                  :fontSize 42
                                  :fill "rgba(255,255,255,0.85)"}
                       :w w
                       :top artist-top
                       :line-spacing line-spacing
                       :max-width max-text-width})]
    (js/fabric.Group. (into-array
                        (concat [cover badge-bg]
                                title-objs
                                artist-objs))
                      (clj->js group-options))))

;; --- MUSIC_OVERLAY_SIMPLE ---
;;
;; A white rounded card: cover thumbnail on the left (darkened by
;; rgba(0,0,0,0.2) with the animated waveform glyph on top), black bold
;; title and near-black regular artist to its right, left-aligned.
;; The font size is fixed; the card grows with the longer text line up
;; to `simple-max-width-ratio`, beyond which the text is shortened with
;; an ellipsis (measured from a story with a long title). All ratios
;; are of the card height, which is the fixed dimension: corner radius
;; 0.089; thumbnail = height minus twice the 0.107 inset, corner radius
;; 0.104; text left edge at 1.12; right padding 0.213; title and artist
;; have the same size (cap height 0.178, ~50 px Arial at our 200 px
;; card height), title black bold, artist regular black at 90% opacity,
;; vertically centered as a block. The thumbnail inset is in
;; `renderer6.music-sticker-constants/simple`, shared with the
;; renderer:
(def simple-height 200)
(def simple-card-radius-ratio 0.089)
(def simple-thumb-radius-ratio 0.104)
(def simple-text-left-ratio 1.12)
(def simple-text-right-pad-ratio 0.213)
(def simple-max-width-ratio 5.54)

(defn simple-sticker
  "The MUSIC_OVERLAY_SIMPLE sticker without the waveform glyph: the
   glyph is not rasterized because the animated waveform video
   (`renderer6.music-sticker-constants/waveform`) is layered over the
   cover thumbnail, both in the editor composition and by renderer6. Rasterized are the white card, the
   darkened cover thumbnail and the title/artist text."
  [{:keys [title artist cover-img]}]
  (let [h simple-height
        r (* simple-card-radius-ratio h)
        inset (* (:thumb-inset-ratio constants/simple) h)
        thumb (- h (* 2 inset))
        thumb-r (* simple-thumb-radius-ratio h)
        text-left (* simple-text-left-ratio h)
        right-pad (* simple-text-right-pad-ratio h)
        max-text-width (- (* simple-max-width-ratio h)
                          text-left
                          right-pad)
        title-obj (-> (js/fabric.Text.
                        title
                        #js {:fontFamily "Arial"
                             :fontWeight "bold"
                             :fontSize 50
                             :fill "#000000"
                             :left text-left
                             :top 44})
                      (truncate-to-width! max-text-width))
        artist-obj (-> (js/fabric.Text.
                         artist
                         #js {:fontFamily "Arial"
                              :fontSize 50
                              :fill "rgba(0,0,0,0.9)"
                              :left text-left
                              :top 112})
                       (truncate-to-width! max-text-width))
        w (+ text-left
             (max (.-width title-obj)
                  (.-width artist-obj))
             right-pad)
        card (js/fabric.Rect.
               #js {:left 0
                    :top 0
                    :width w
                    :height h
                    :rx r
                    :ry r
                    :fill "#ffffff"})
        cover (cover-image cover-img
                           {:left inset
                            :top inset
                            :size thumb
                            :radius thumb-r})
        tint (js/fabric.Rect.
               #js {:left inset
                    :top inset
                    :width thumb
                    :height thumb
                    :rx thumb-r
                    :ry thumb-r
                    :fill "rgba(0,0,0,0.2)"})]
    (js/fabric.Group. (into-array [card
                                   cover
                                   tint
                                   title-obj
                                   artist-obj])
                      (clj->js group-options))))
